import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MailWarning, Inbox, Archive, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_ORDER = ["all", "new", "read", "archived"];

export default function AdminFeedback() {
  const { lang } = useLang();
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/feedback?status=${status}&page=1&page_size=100`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); setEmailConfigured(!!r.data.email_configured); })
      .catch((e) => toast.error(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const setItemStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/feedback/${id}`, { status: newStatus });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <AdminLayout>
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-terracotta-700 mb-1"><Inbox className="w-4 h-4" /> Feedback</div>
          <h1 className="font-display text-3xl tracking-tight">{lang === "id" ? "Pusat Umpan Balik" : "Feedback Center"}</h1>
          <div className="text-xs text-muted-foreground mt-1">{total} {lang === "id" ? "pesan" : "messages"}</div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="admin-feedback-status-filter" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!emailConfigured && (
        <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-3" data-testid="admin-feedback-email-notice" data-wrap="responsive">
          <MailWarning className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">{lang === "id" ? "Penerusan email belum aktif." : "Email forwarding not active."}</div>
            <div className="text-xs mt-0.5">{lang === "id" ? "Setel RESEND_API_KEY di backend/.env dan support_email di Pengaturan Situs untuk otomatis menerima email umpan balik. Semua pesan tetap tersimpan di dashboard ini." : "Set RESEND_API_KEY in backend/.env and support_email in Site Settings to auto-receive feedback emails. All messages remain saved here."}</div>
          </div>
        </div>
      )}

      {loading ? <div className="text-muted-foreground">…</div> : (
        <div className="space-y-3" data-testid="admin-feedback-list">
          {items.length === 0 && (
            <div className="p-8 text-center text-muted-foreground rounded-2xl border border-dashed border-border">{lang === "id" ? "Belum ada pesan pada filter ini." : "No messages for this filter."}</div>
          )}
          {items.map((it) => (
            <div key={it.id} className="p-5 rounded-2xl border border-border bg-card" data-testid={`admin-feedback-row-${it.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">{it.category}</Badge>
                <Badge variant={it.status === "new" ? "default" : "secondary"} className={it.status === "new" ? "bg-terracotta-500 text-cream-50" : ""}>{it.status}</Badge>
                <div className="text-xs text-muted-foreground ml-2 break-words">{it.name || (lang === "id" ? "Anonim" : "Anonymous")} · <a href={`mailto:${it.email}`} className="underline">{it.email}</a></div>
                <div className="text-xs text-muted-foreground ml-auto">{(it.created_at || "").slice(0, 19).replace("T", " ")} UTC</div>
              </div>
              <div className="mt-3 text-sm leading-relaxed break-words whitespace-pre-line" data-wrap="responsive">{it.message}</div>
              {it.context_url && (
                <a href={it.context_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-terracotta-700 hover:underline"><ExternalLink className="w-3 h-3" /> {it.context_url}</a>
              )}
              {it.forwarded_at && (
                <div className="mt-2 text-[11px] text-muted-foreground">{lang === "id" ? "Diteruskan lewat email" : "Forwarded via email"}: {it.forward_ok ? "✓" : "✗"} · {it.forwarded_at.slice(0, 19).replace("T", " ")}</div>
              )}
              <div className="mt-3 flex gap-2">
                {it.status !== "read" && <Button size="sm" variant="outline" onClick={() => setItemStatus(it.id, "read")} data-testid={`fb-read-${it.id}`}><Check className="w-3.5 h-3.5 mr-1" /> {lang === "id" ? "Tandai dibaca" : "Mark read"}</Button>}
                {it.status !== "archived" && <Button size="sm" variant="ghost" onClick={() => setItemStatus(it.id, "archived")} data-testid={`fb-archive-${it.id}`}><Archive className="w-3.5 h-3.5 mr-1" /> {lang === "id" ? "Arsipkan" : "Archive"}</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
